import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, Check } from 'lucide-react';

/**
 * Single-select personnel picker for the BRC AAR Work Details WDB / QVB fields.
 * Backed by GET /api/car-files/brc/personnel-options — the backend hides any
 * user the caller cannot pick, so this modal renders only eligible candidates.
 *
 * Props:
 *   open, onClose
 *   field        'WDB' | 'QVB'  (drives the modal title)
 *   facilityId   BRC's responding facility (for backend scoping)
 *   api          axios-style client from useAuth
 *   selectedUserId  optional — currently saved user reference, for the check mark
 *   onSelect(user) — called with {id, first_name, last_name, display, role}
 */
export default function PersonnelPickerModal({
  open,
  onClose,
  field,
  facilityId,
  api,
  selectedUserId,
  onSelect,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setError('');
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/car-files/brc/personnel-options', {
          params: facilityId ? { facility_id: facilityId } : {},
        });
        setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load personnel');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, api, facilityId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) =>
      (o.display || '').toLowerCase().includes(q) ||
      (o.role || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const title = field === 'QVB' ? 'Quality Verified By' : 'Work Done By';
  const description =
    field === 'QVB'
      ? 'Select the person who verified the quality of this line item'
      : 'Select the person who did the work on this line item.';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid={`personnel-picker-${field}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or role…"
            className="pl-8"
            data-testid={`personnel-picker-search-${field}`}
          />
        </div>

        <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="p-4 text-sm text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-gray-500" data-testid={`personnel-picker-empty-${field}`}>
              {options.length === 0
                ? 'No personnel are available for selection.'
                : 'No matches for your search.'}
            </div>
          ) : (
            filtered.map((p) => {
              const isSelected = p.id === selectedUserId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onSelect(p); onClose(); }}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  data-testid={`personnel-picker-option-${field}-${p.id}`}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{p.display}</div>
                    <div className="text-xs text-gray-500">{p.role}</div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} data-testid={`personnel-picker-close-${field}`}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
